import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { Text, Icon } from '@components/base/display';
import { DataList, useDataList, dataListWorkflowRegistry } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { createTaskListConfig } from '../configs/taskListConfig';
import { useClaimFlowAuthStore } from '../auth/store';

interface TaskListCardProps {
  /**
   * Per-tenant column layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the column config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
}

export function TaskListCard({ _layoutOverrides }: Readonly<TaskListCardProps> = {}) {
  const sessionId = useClaimFlowAuthStore((s) => s.spoofedSessionId);
  const baseConfig = useMemo(() => createTaskListConfig(), []);
  usePublishLayoutConfig(
    'claimflow.taskListCard',
    buildPublishedConfigFromDataList(baseConfig, 'Tâches en cours'),
  );
  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );
  const list = useDataList(config);
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  useEffect(() => {
    dataListWorkflowRegistry.registerRowClickHandler(
      'claimflow-taskDetail',
      (row: Record<string, unknown>) => {
        const taskId = row.ID_EVENT;
        const rawIdSin = row.ID_SIN;
        const idSin = typeof rawIdSin === 'string' || typeof rawIdSin === 'number'
          ? String(rawIdSin) : undefined;
        navigate(`/claimflow/${tenantSlug}/task/${taskId}?id_sin=${idSin}`, {
          state: {
            ses: sessionId == null ? undefined : String(sessionId),
            id_sin: idSin,
          },
        });
      },
    );
  }, [navigate, tenantSlug, sessionId]);

  return (
    // `className` is appended after the DataList root's hardcoded
    // `flex flex-col gap-3`. The surface classes match ClaimSummaryCard's base
    // `Card` (rounded-lg + surfaceVariantClasses.default) so the whole list
    // reads as one self-contained card; `overflow-hidden` clips the Table/Footer
    // bottom corners to the radius. `!gap-0` collapses the root's `gap-3` so the
    // header/toolbar/table/footer sit flush — the `!important` is required
    // because, at equal specificity, Tailwind's later-emitted `.gap-3` rule
    // would otherwise win over a plain `.gap-0`.
    <DataList
      config={config}
      list={list}
      className="!gap-0 overflow-hidden rounded-lg border border-secondary-300 bg-secondary-100 shadow-sm"
    >
      <div className="flex items-center gap-2 bg-secondary-50 border-b border-secondary-200 px-4 py-3 rounded-t-lg">
        <Icon icon={ClipboardList} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Tâches en cours', _layoutOverrides)}
        </Text>
        <Text as="span" size="sm" color="muted">
          ({list.totalItems} tâches)
        </Text>
      </div>
      <DataList.Toolbar className="px-4 py-3" />
      <DataList.Table />
      <DataList.Footer />
    </DataList>
  );
}
